/**
 * Formato de fechas para toda la app: corto dd/mm/yyyy, largo, y con hora.
 * Las fechas solo (YYYY-MM-DD) se interpretan en calendario local para evitar desfase UTC.
 */

/** YYYY-MM-DD del día actual en calendario local (inputs type="date", atributo min, etc.). */
export function todayLocalDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parsea valores para mostrar: YYYY-MM-DD solo → local; DD/MM/YYYY; ISO con hora → instante local.
 */
export function parseInputToDate(input: string | Date | null | undefined): Date | null {
  if (input == null || input === '') return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;

  const t = String(input).trim();
  if (!t) return null;

  const dmy = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]) - 1;
    const year = Number(dmy[3]);
    const dt = new Date(year, month, day);
    if (dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === day) return dt;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const [y, m, d] = t.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) return dt;
  }

  const dt = new Date(t);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Formato corto: 01/04/2026
 */
export function formatDate(date: string | Date | null | undefined): string {
  const d = parseInputToDate(date);
  if (!d) return '—';
  return d.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formato largo: 1 de abril de 2026
 */
export function formatDateLong(date: string | Date | null | undefined): string {
  const d = parseInputToDate(date);
  if (!d) return '—';
  return d.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Formato con hora: 01/04/2026 14:30 (24 h)
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  const d = parseInputToDate(date);
  if (!d) return '—';
  return d.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Mes y año (p. ej. contratos): abr 2026 */
export function formatMonthYear(date: string | Date | null | undefined): string {
  const d = parseInputToDate(date);
  if (!d) return '—';
  return d.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
}

/** Mes largo + año: abril de 2026 (tablas de pagos por periodo) */
export function formatMonthYearLong(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('es-MX', {
    month: 'long',
    year: 'numeric',
  });
}

// --- Compatibilidad con imports antiguos ---

/** @deprecated Usar formatDateLong */
export function formatDateSpanishLong(dateString: string | Date): string {
  return formatDateLong(dateString);
}

/** @deprecated Usar formatDate */
export function formatFechaCorta(dateString: string | Date): string {
  return formatDate(dateString);
}

/** @deprecated Usar formatDateTime */
export function formatDateTimeCorta(dateString: string | Date): string {
  return formatDateTime(dateString);
}
